/**
 * Catalog label/format helpers shared by the Items / Packages / Discounts tabs
 * and the RecordDrawer. Pure functions only (no React) — mirrors the Cinch
 * billing enums (CIN-6317). Cinch serializes fractions (e.g. package
 * `estimated_gross_margin` 0–1); `marginPct` renders those as a percentage.
 */
import type { DiscountRow } from "./api";

// ── Item category (Cinch Item.category) ──────────────────────────────────────
export const ITEM_CATEGORIES: { value: string; label: string }[] = [
  { value: "subscription", label: "Subscription" },
  { value: "usage", label: "Usage" },
  { value: "pass_thru", label: "Pass-thru" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other" },
];

export const ITEM_CATEGORY_OPTIONS = ITEM_CATEGORIES;

/** Archive state for the catalog list filters. Maps onto Cinch's single `status`
 *  filter (active | archived | all): no selection = active-only (the backend
 *  default), one option = that state, both = all. */
export const ARCHIVE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const CATEGORY_LABEL = new Map(ITEM_CATEGORIES.map((c) => [c.value, c.label]));

export function categoryLabel(v: string | null | undefined): string {
  if (!v) return "—";
  return CATEGORY_LABEL.get(v) ?? "Other";
}

/** Integration keys are free-form public-schema keys (e.g. "hubspot"); title-case them. */
export function integrationLabels(keys: string[] | null | undefined): string {
  if (!keys?.length) return "—";
  return keys.map((k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())).join(", ");
}

// ── Package pricing type ─────────────────────────────────────────────────────
export function pricingTypeLabel(v: string | null | undefined): string {
  if (v === "flat_fee") return "Flat Fee";
  if (v === "per_unit") return "Per Unit";
  return v || "—";
}

/** A Cinch fraction (0–1) rendered as a percentage, e.g. 0.42 → "42%". */
export function marginPct(fraction: number | null | undefined): string {
  if (fraction == null) return "—";
  return `${(Number(fraction) * 100).toFixed(1)}%`;
}

// ── Discount type (Cinch DiscountType) ───────────────────────────────────────
export const DISCOUNT_TYPES: { value: string; label: string }[] = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed", label: "Fixed" },
  { value: "tiered", label: "Tiered" },
  { value: "time_based", label: "Time-based" },
  { value: "quantity_based", label: "Quantity-based" },
];

export const DISCOUNT_TYPE_OPTIONS = DISCOUNT_TYPES;

const DISCOUNT_TYPE_LABEL = new Map(DISCOUNT_TYPES.map((d) => [d.value, d.label]));

export function discountTypeLabel(v: string | null | undefined): string {
  if (!v) return "—";
  return DISCOUNT_TYPE_LABEL.get(v) ?? v;
}

/** Human-readable discount value (mirrors the proposals `displayValue` helper). */
export function discountValueLabel(d: Pick<DiscountRow, "type" | "value" | "duration_periods" | "period_unit">): string {
  const v = d.value;
  switch (d.type) {
    case "percentage":
    case "tiered":
      return v == null ? "—" : `${v}%`;
    case "fixed":
      return v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "time_based": {
      const periods = d.duration_periods;
      const unit = d.period_unit || "month";
      const pct = v == null ? "" : `${v}% `;
      return periods ? `${pct}for ${periods} ${unit}${periods === 1 ? "" : "s"}` : `${pct}(time-based)`;
    }
    case "quantity_based":
      return "Unit grants";
    default:
      return v == null ? "—" : String(v);
  }
}
